import { dispatchAction } from '@ardoq/rxbeach';
import {
  CreatedItemsLoadedStateParams,
  isCreatedItemsLoadedState,
  LoadedState,
  TraversalParams,
  LoadedStateParams,
  LoadedGraphChunk,
  WithTransactionId,
  ComponentSelection,
} from '@ardoq/api-types';
import { scopeDataOperations } from '@ardoq/scope-data';
import { enterViewpointMode } from 'streams/traversals/actions';
import { clearAndSetLoadedState, registerLoadedState } from './actions';
import { advancedSearchApi, traversalApi } from '@ardoq/api';
import { scopeDataLoaded } from 'traversals/stagedLoadedDataAndState$';
import {
  hasStartSet,
  hasStartQuery,
  traversalParamsToLoadTraversalParams,
} from '@ardoq/api-types';
import { isArdoqError, toArdoqError } from '@ardoq/common-helpers';
import { logBuildLoadedStateError } from './logBuildTraversalError';
import { TraversalErrorReason, type TraversalError } from './traversalTypes';
import { loadedStateTracking } from './loadedStateTracking';
import { logErrorIfInconsistentDataForScopeData } from 'utils/logErrorIfInconsistentData';
import { getStartSetScopeData } from './getStartSetScopeData';
import { toComponentPaths } from 'traversals/toFEComponentPaths';
import { loadedStateOperations } from './loadedStateOperations';

const hasNoStartContext = (componentSelection: ComponentSelection): boolean =>
  !hasStartSet(componentSelection) && !hasStartQuery(componentSelection);

export const buildStateForCreatedItems = async (
  params: CreatedItemsLoadedStateParams,
  abortSignal?: AbortSignal
): Promise<LoadedGraphOrError> => {
  const { componentIds, referenceIds } = params.data;
  const scopeDataOrError = await advancedSearchApi.scopeData(
    {
      componentIds,
      referenceIds,
    },
    abortSignal
  );
  if (isArdoqError(scopeDataOrError)) {
    return scopeDataOrError.isAborted
      ? {
          error: 'Aborted',
          reason: TraversalErrorReason.ABORTED,
          ardoqError: scopeDataOrError,
        }
      : {
          error: 'Failed to load created items loaded state',
          reason: TraversalErrorReason.UNKNOWN,
          ardoqError: scopeDataOrError,
        };
  }

  logErrorIfInconsistentDataForScopeData({
    components: scopeDataOrError.components,
    scopeData: scopeDataOrError,
    isLogErrors: true,
  });

  return {
    scopeData: scopeDataOrError,
    componentIdsAndReferences: {
      componentIds: scopeDataOrError.scopeComponents,
      references: referenceIds.length > 0 ? scopeDataOrError.references : [],
      parentChildReferences: [],
      startSetResult: scopeDataOrError.scopeComponents,
      pathResults: [],
    },
  };
};

export const loadTraversalState = async (
  params: TraversalParams,
  abortSignal?: AbortSignal
): Promise<LoadedGraphOrError> => {
  try {
    if (hasNoStartContext(params.componentSelection)) {
      return loadedStateOperations.getEmptyLoadedGraph();
    }

    const result = await traversalApi.loadTraversal(
      traversalParamsToLoadTraversalParams(params),
      abortSignal
    );

    if (isArdoqError(result)) {
      return result.isAborted
        ? {
            error: 'Aborted',
            reason: TraversalErrorReason.ABORTED,
            ardoqError: result,
          }
        : {
            error: 'Failed to load traversal',
            reason: TraversalErrorReason.UNKNOWN,
            ardoqError: result,
          };
    }

    const { scopeData: rawScopeData, startSetResult, pathResults } = result;
    logErrorIfInconsistentDataForScopeData({
      components: rawScopeData.components,
      scopeData: rawScopeData,
      isLogErrors: true,
    });

    const { scopeData, parentChildReferences } =
      scopeDataOperations.partitionParentChildReferences(rawScopeData);
    const startSetScopeData = await getStartSetScopeData(params, scopeData);

    return {
      scopeData: scopeDataOperations.merge(scopeData, {
        ...startSetScopeData,
        scopeComponents: [],
      }),
      componentIdsAndReferences: {
        componentIds: scopeData.scopeComponents,
        references: scopeData.references,
        parentChildReferences,
        startSetResult,
        pathResults: toComponentPaths(
          params.paths,
          pathResults ?? [],
          rawScopeData.references
        ),
      },
    };
  } catch (e) {
    const error = e instanceof Error ? e : new Error(String(e));
    return {
      error:
        error.message || 'Unknown error happened when building traversal state',
      reason: TraversalErrorReason.UNKNOWN,
      ardoqError: toArdoqError({ error }),
    };
  }
};

export const buildTraversalState = async (
  params: TraversalParams,
  abortSignal?: AbortSignal
): Promise<LoadedGraphOrError> => {
  const result = await loadTraversalState(params, abortSignal);
  if (!isError(result) && !hasNoStartContext(params.componentSelection)) {
    loadedStateTracking.trackExecutionResult(result.componentIdsAndReferences);
  }
  return result;
};

export type LoadedGraphOrError = TraversalError | LoadedGraphChunk;

type RebuildAndRegisterStateArgs = WithTransactionId & {
  loadedStates: LoadedStateParams[];
  abortSignal?: AbortSignal;
};

export const rebuildAndSetState = async ({
  loadedStates,
  abortSignal,
  transactionId,
}: RebuildAndRegisterStateArgs): Promise<LoadedState[] | TraversalError> => {
  const result = await rebuildStateInternal({
    loadedStates,
    abortSignal,
  });
  if (!isError(result)) {
    dispatchAction(
      clearAndSetLoadedState({
        loadedStates: result,
        transactionId,
      })
    );
  }

  return result;
};

export const rebuildAndRegisterState = async ({
  loadedStates,
  abortSignal,
}: RebuildAndRegisterStateArgs): Promise<LoadedState[] | TraversalError> => {
  const result = await rebuildStateInternal({
    loadedStates,
    abortSignal,
  });
  if (!isError(result)) {
    dispatchAction(registerLoadedState({ loadedStates: result }));
  }
  return result;
};

const rebuildStateInternal = async ({
  loadedStates,
  abortSignal,
}: RebuildAndRegisterStateArgs): Promise<LoadedState[] | TraversalError> => {
  dispatchAction(enterViewpointMode());
  const result = await rebuildActiveState({
    states: loadedStates,
    abortSignal,
  });
  if (isError(result)) {
    logBuildLoadedStateError(result, loadedStates);
    return result;
  }

  const trackingLocation = 'directURLVisit';
  result.map(({ scopeData }) => {
    dispatchAction(scopeDataLoaded({ scopeData, trackingLocation }));
  });

  return result;
};

type RebuildActiveStateArgs = {
  states: LoadedStateParams[];
  abortSignal?: AbortSignal;
};

export const isError = (x: unknown): x is TraversalError =>
  Boolean(x && typeof x === 'object' && 'error' in x && x.error);

export const rebuildActiveState = async ({
  states,
  abortSignal,
}: RebuildActiveStateArgs) => {
  const results: LoadedState[] = [];
  for (const state of states) {
    const loadedGraphOrError = isCreatedItemsLoadedState(state)
      ? await buildStateForCreatedItems(state, abortSignal)
      : await buildTraversalState(
          loadedStateOperations.toTraversalParams(state),
          abortSignal
        );
    if (isError(loadedGraphOrError)) {
      return loadedGraphOrError;
    }
    results.push(
      loadedStateOperations.attachLoadedGraph(state, loadedGraphOrError)
    );
  }

  return results;
};
